import { Component } from 'react';
import { FormattedDate, FormattedMessage, useIntl } from 'react-intl';
import { isNil, has } from 'lodash';
import { fromJS } from 'immutable';

// COMPONENTS
import DropdownMenu, { CLASSES } from 'components/DropdownMenu';
import MaybeTranslation from 'components/MaybeTranslation';

// CONFIGS
import StringConfiguration from './String';
import NumberConfiguration from './Number';
import BooleanConfiguration from './Boolean';
import DateConfiguration from './Date';
import DropdownConfiguration from './Dropdown';
import FuelDropdownConfiguration from './FuelDropdown';
import ConnectivityConfiguration from './Connectivity';

// STYLES
import { Title, NoFiltersMessage, ChooseFilter, AppliedFilterContainer, AppliedFilterValue, CloseIcon } from './styles';

// CONSTANTS
import { FILTER_TYPES, FILTER_FORM } from 'constants/filters';

const NewFilterSelection = (props) => (
  <ChooseFilter data-testid="filters__add-filter-input">
    <DropdownMenu
      hidden={!props.visible}
      renderedValue={{
        translationId: 'components.Filters.dropdownPlaceholder',
      }}
      items={props.items}
      className={`${CLASSES.FULL_WIDTH}`}
      maxItems={8}
      input
    />
  </ChooseFilter>
);

const FilterList = (props) => {
  const { formatDate } = useIntl();

  if (props.filters.size === 0) {
    return (
      <NoFiltersMessage>
        <FormattedMessage id="components.Filters.noFilters" />
      </NoFiltersMessage>
    );
  }

  const filters = props.filters.map((filter, key) => {
    let filterValue = filter.get('value');
    let showFieldName = true;
    let showFilterValue = true;
    let fieldName = { translationId: 'unknownField' };
    let comparator = 'unknownField';
    let renderedValue = null;

    const fieldObject = props.fields
      .find((field) => field.get('name') === filter.get('field'), null, fromJS({}))
      .toJS();

    if (fieldObject) {
      fieldName = fieldObject.title;
      if (fieldObject.deserialize) {
        filterValue = fieldObject.deserialize(filterValue);
      }
    }

    // Handle different filter types
    if (filter.get('comparator') === 'isnull') {
      comparator = Number(filterValue) === 0 ? 'isKnown' : 'isUnknown';
      // Handle Connectivity filter
    } else if (filter.get('field') === 'is_connected') {
      showFieldName = false;
      showFilterValue = false;
      switch (filterValue) {
        case 1:
        case '1':
          comparator = 'isConnected';
          break;
        case 0:
        case '0':
          comparator = 'isDisconnected';
          break;
        default:
          comparator = 'connectivityIsUnknown';
      }
      // Handle Boolean filter
    } else if (filter.get('comparator') === 'exact' && fieldObject && fieldObject.type === 'boolean') {
      comparator = Number(filterValue) === 1 ? 'isTrue' : 'isFalse';
      showFilterValue = false;
    } else if (fieldObject && Object.keys(fieldObject).length !== 0) {
      const filterObject = FILTER_FORM[fieldObject.type].find(
        (filterOption) => filterOption.comparator === filter.get('comparator')
      );

      if (filterObject) {
        ({ translation: comparator } = filterObject);
      } else {
        ({ comparator } = filter);
      }
    } else {
      ({ comparator } = filter);
    }

    // Finally, dropdown item values need to be mapped properly so that the
    // user gets a pretty display rather than a number
    if (
      fieldObject &&
      (fieldObject.type === FILTER_TYPES.dropdown || fieldObject.type === FILTER_TYPES.fuelDropdown) &&
      fieldObject.values &&
      has(fieldObject.values, filterValue)
    ) {
      renderedValue = <FormattedMessage id={fieldObject.values[filterValue].translationId} />;
    } else if (!isNil(filterValue) && typeof filterValue === 'string' && filter.get('comparator') !== 'isnull') {
      renderedValue = filterValue.replace(/%20/g, ' ');
    }

    // Show dateTime in local time
    if (fieldObject && fieldObject.type === FILTER_TYPES.dateTime) {
      if (filter.get('comparator') === 'range') {
        const [fromDate, toDate] = filterValue.split(',');
        // Would like to use <FormattedDateTimeRange /> here, but CLDR seems to have a bug and shows en-US styles.
        // as `M/d/y - M/d/y` instead of `y-M-d - y-M-d` for en-CA.
        renderedValue = `${formatDate(new Date(fromDate))} - ${formatDate(new Date(toDate))}`;
      } else {
        renderedValue = <FormattedDate value={new Date(filterValue)} />;
      }
    }

    return (
      // eslint-disable-next-line react/no-array-index-key
      <AppliedFilterContainer key={key}>
        {showFieldName && <MaybeTranslation renderedValue={fieldName} />}
        &nbsp;
        <FormattedMessage id={`components.Filters.${comparator}`} />
        &nbsp;
        {showFilterValue && <AppliedFilterValue>{renderedValue}</AppliedFilterValue>}
        <CloseIcon onClick={props.onRemoveFilter(key, filter)} className="icon-close-light" />
      </AppliedFilterContainer>
    );
  });

  return <div>{filters}</div>;
};

const FilterForm = (props) => {
  const { column, onCancel, onSave } = props;

  if (!column) {
    return null;
  }

  switch (column.get('type')) {
    case FILTER_TYPES.string:
    case FILTER_TYPES.phone:
      // @ts-expect-error TS(2322): Type '{ column: any; onCancel: any; onSave: any; }... Remove this comment to see the full error message
      return <StringConfiguration column={column} onCancel={onCancel} onSave={onSave} />;
    case FILTER_TYPES.number:
    case FILTER_TYPES.money:
      // @ts-expect-error TS(2322): Type '{ column: any; onCancel: any; onSave: any; }... Remove this comment to see the full error message
      return <NumberConfiguration column={column} onCancel={onCancel} onSave={onSave} />;
    case FILTER_TYPES.boolean:
      // @ts-expect-error TS(2769): No overload matches this call.
      return <BooleanConfiguration column={column} onCancel={onCancel} onSave={onSave} />;
    case FILTER_TYPES.dateTime:
    case FILTER_TYPES.date:
    case FILTER_TYPES.humanizedDate:
      // @ts-expect-error TS(2322): Type '{ column: any; onCancel: any; onSave: any; }... Remove this comment to see the full error message
      return <DateConfiguration column={column} onCancel={onCancel} onSave={onSave} />;
    case FILTER_TYPES.dropdown:
      // @ts-expect-error TS(2769): No overload matches this call.
      return <DropdownConfiguration column={column} onCancel={onCancel} onSave={onSave} />;
    case FILTER_TYPES.connectivity:
      // @ts-expect-error TS(2769): No overload matches this call.
      return <ConnectivityConfiguration column={column} onCancel={onCancel} onSave={onSave} />;
    case FILTER_TYPES.fuelDropdown:
      // @ts-expect-error TS(2769): No overload matches this call.
      return <FuelDropdownConfiguration column={column} onCancel={onCancel} onSave={onSave} />;
    default:
      return null;
  }
};

class Filters extends Component {
  state = {
    filterSelected: null,
  };

  // The different columns the filters can be applied on
  getFieldsForDropdown = () =>
    // @ts-expect-error TS(2339): Property 'fields' does not exist on type 'Readonly... Remove this comment to see the full error message
    this.props.fields
      .filter((field) => field.get('filterable') !== false)
      .map((field) =>
        fromJS({
          id: field.get('name'),
          renderedValue: field.get('title'),
          handleClick: this.handleChooseField(field),
        })
      );

  // Selects a filter to add
  handleChooseField = (field) => () => {
    // @ts-expect-error TS(2339): Property 'onAddingFilter' does not exist on type '... Remove this comment to see the full error message
    this.props.onAddingFilter(Boolean(field));
    this.setState({ filterSelected: field });
  };

  // Saves the filter with the given settings
  handleSaveFilter = (filter) => {
    const newFilter = { ...filter.toJS() };
    if (filter) {
      // @ts-expect-error TS(2339): Property 'fields' does not exist on type 'Readonly... Remove this comment to see the full error message
      const fieldConfig = this.props.fields.find((field) => field.get('name') === filter.get('field')).toJS();
      if (fieldConfig && fieldConfig.serialize) {
        newFilter.value = fieldConfig.serialize(filter.get('value'));
      }
    }
    // @ts-expect-error TS(2339): Property 'onAddFilter' does not exist on type 'Rea... Remove this comment to see the full error message
    this.props.onAddFilter(fromJS(newFilter));
    this.setState({ filterSelected: null });
  };

  // Removes a filter from the list
  handleRemoveFilter = (index, filter) => () => {
    // @ts-expect-error TS(2339): Property 'onRemoveFilter' does not exist on type '... Remove this comment to see the full error message
    this.props.onRemoveFilter(index, filter);
  };

  render() {
    const filterItems = this.getFieldsForDropdown();

    return (
      <div>
        <Title>
          <FormattedMessage id="components.Filters.title" />
        </Title>

        {/* @ts-expect-error TS(2339): Property 'fields' does not exist on type 'Readonly... Remove this comment to see the full error message */}
        <FilterList fields={this.props.fields} filters={this.props.filters} onRemoveFilter={this.handleRemoveFilter} />

        <NewFilterSelection
          visible={!this.state.filterSelected}
          onSelectFilter={this.handleChooseField}
          items={filterItems}
        />

        <FilterForm
          column={this.state.filterSelected}
          onCancel={this.handleChooseField(null)}
          onSave={this.handleSaveFilter}
        />
      </div>
    );
  }
}

export default Filters;
